import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { companyInfo } from '../data/mock';
import { FileText, Shield, Scale, AlertCircle } from 'lucide-react';

const LegalPage = () => {
  const { theme } = useTheme();

  const legalSections = [
    {
      icon: Shield,
      title: 'Privacy Policy',
      description: 'Learn how we collect, use, and protect your personal information.',
      link: '/privacy'
    },
    {
      icon: FileText,
      title: 'Terms & Conditions',
      description: 'Read our terms of service and user agreements.',
      link: '/terms'
    }
  ];

  return (
    <div className={`min-h-screen pt-24 md:pt-32 pb-16 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Legal Information
          </h1>
          <p className={`text-lg ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-600'
          }`}>
            Important legal documents and policies for RKS Technologies
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid sm:grid-cols-2 gap-6 mb-16">
          {legalSections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link
                key={index}
                to={section.link}
                className={`p-6 transition-all duration-300 group ${
                  theme === 'dark'
                    ? 'bg-[#0a0a0a] border border-white/10 hover:border-[#00FFD1]/40'
                    : 'bg-white border border-gray-200 hover:border-[#00997A]/40 hover:shadow-lg'
                }`}
              >
                <div className={`p-3 w-fit mb-4 ${
                  theme === 'dark' ? 'bg-[#00FFD1]/10' : 'bg-[#00997A]/10'
                }`}>
                  <Icon className={`w-6 h-6 ${
                    theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                  }`} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {section.title}
                </h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                }`}>
                  {section.description}
                </p>
              </Link>
            );
          })}        </div>

        {/* Disclaimers */}
        <div className={`p-6 mb-10 ${
          theme === 'dark' ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`p-2 ${
              theme === 'dark' ? 'bg-yellow-500/10' : 'bg-yellow-50'
            }`}>
              <AlertCircle className={`w-5 h-5 ${
                theme === 'dark' ? 'text-yellow-500' : 'text-yellow-600'
              }`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Disclaimer
              </h3>
              <p className={`text-sm leading-relaxed ${
                theme === 'dark' ? 'text-white/60' : 'text-gray-600'
              }`}>
                The information provided on this website is for general informational purposes only. While we strive to keep the information up to date and accurate, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information, products, services, or related graphics contained on the website.
              </p>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <section className="mb-10">
          <h2 className={`text-xl sm:text-2xl font-semibold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Company Information
          </h2>
          
          <div className={`p-6 ${
            theme === 'dark' ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'
          }`}>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>Company Name</h4>
                <p className={`${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>RKS Technologies</p>
              </div>
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>Business Type</h4>
                <p className={`${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>IT Consulting & Staffing</p>
              </div>
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>Location</h4>
                <p className={`${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>{companyInfo.location}</p>
              </div>
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>Contact Email</h4>
                <a href={`mailto:${companyInfo.email}`} className={`${
                  theme === 'dark' ? 'text-[#00FFD1] hover:text-[#00FFD1]/80' : 'text-[#00997A] hover:text-[#007A61]'
                }`}>{companyInfo.email}</a>
              </div>
            </div>
          </div>
        </section>

        {/* Copyright Notice */}
        <section className="mb-10">
          <h2 className={`text-xl sm:text-2xl font-semibold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Copyright Notice
          </h2>
          <p className={`leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            © {companyInfo.year} RKS Technologies. All rights reserved. All content, trademarks, logos, and intellectual property displayed on this website are the property of RKS Technologies or their respective owners. No part of this website may be reproduced, distributed, or transmitted in any form without prior written permission.
          </p>
        </section>

        {/* Equal Opportunity */}
        <section className="mb-10">
          <h2 className={`text-xl sm:text-2xl font-semibold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Equal Opportunity Employer
          </h2>
          <p className={`leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            RKS Technologies is an equal opportunity employer. We do not discriminate based on race, color, religion, sex, national origin, age, disability, veteran status, sexual orientation, gender identity, or any other characteristic protected by applicable law. We are committed to creating a diverse and inclusive workplace.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className={`text-xl sm:text-2xl font-semibold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Legal Inquiries
          </h2>
          <p className={`mb-4 leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            For any legal inquiries or concerns, please contact us:
          </p>
          <div className={`p-6 ${
            theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-gray-100 border border-gray-200'
          }`}>
            <p className={`mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}><strong>RKS Technologies - Legal Department</strong></p>
            <p className={`mb-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Email: {companyInfo.email}</p>
            <p className={`mb-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Phone: {companyInfo.phone}</p>
            <p className={`${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Address: {companyInfo.location}</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LegalPage;
